/**
 * Capacitor OAuth Handler
 * Handles OAuth callbacks via deep links for Capacitor mobile apps
 */

import { App } from '@capacitor/app';
import type { URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { authService } from './authService';
import type { LoginResponse } from './authService';
import { appendPostLoginRedirect, getPostLoginRedirect, sanitizePostLoginRedirect } from './authRedirect';
import { getPublicRuntimeConfig } from './publicRuntimeConfig';

function setPendingValue(key: string, value: string, options?: { encode?: boolean; maxAge?: number }) {
  const { encode = false, maxAge = 60 * 5 } = options || {};
  const storedValue = encode ? encodeURIComponent(value) : value;

  localStorage.setItem(key, storedValue);
  document.cookie = `${key}=${storedValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearPendingValue(key: string) {
  localStorage.removeItem(key);
  document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

export class CapacitorOAuthHandler {
  private listener: PluginListenerHandle | null = null;
  private isProcessing = false;
  private processingTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Start listening for OAuth callback deep links
   * Call this when the login page mounts
   */
  async startListening(): Promise<void> {
    // Only run in Capacitor environment
    if (typeof window === 'undefined' || !Capacitor.isNativePlatform()) {
      return;
    }

    console.log('[CapacitorOAuth] Starting App URL listener for OAuth callbacks');

    // Remove existing listener if any (prevent duplicates)
    if (this.listener) {
      await this.listener.remove();
      this.listener = null;
    }

    // Reset processing flag and timeout
    this.isProcessing = false;
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }

    // Listen for app URL open events (deep links)
    this.listener = await App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      console.log('[CapacitorOAuth] App URL opened:', event.url);
      
      // Check if this is an OAuth callback
      if (event.url.startsWith('totalaccess://oauth-callback')) {
        await this.processOAuthCallback(event.url);
      }
    });

    console.log('[CapacitorOAuth] Listener registered successfully');
  }

  /**
   * Public entry point so global deep-link handling can safely hand off OAuth callbacks.
   */
  async processOAuthCallback(url: string): Promise<void> {
    await this.handleOAuthCallback(url);
  }

  /**
   * Stop listening for OAuth callbacks
   * Call this when the login page unmounts or after successful auth
   */
  async stopListening(): Promise<void> {
    if (this.listener) {
      console.log('[CapacitorOAuth] Stopping App URL listener');
      await this.listener.remove();
      this.listener = null;
    }
    
    // Reset processing state
    this.isProcessing = false;
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout);
      this.processingTimeout = null;
    }
  }

  /**
   * Handle OAuth callback from deep link
   */
  private async handleOAuthCallback(url: string): Promise<void> {
    // Prevent duplicate processing
    if (this.isProcessing) {
      console.log('[CapacitorOAuth] Already processing callback, ignoring duplicate');
      return;
    }

    this.isProcessing = true;

    // Auto-reset processing flag after 10 seconds (in case of errors)
    this.processingTimeout = setTimeout(() => {
      console.log('[CapacitorOAuth] Auto-resetting processing flag after timeout');
      this.isProcessing = false;
    }, 10000);

    try {
      console.log('[CapacitorOAuth] Processing OAuth callback URL:', url);

      // Close the system browser (may already be closed)
      try {
        await Browser.close();
        console.log('[CapacitorOAuth] Closed system browser');
      } catch (error) {
        console.warn('[CapacitorOAuth] Browser already closed or failed to close:', error);
      }

      // Wait for browser close animation to complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Parse URL parameters
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      console.log('[CapacitorOAuth] Callback parameters:', Object.fromEntries(params.entries()));

      // Check for error parameters first (e.g., no_account)
      const error = params.get('error');
      const errorEmail = params.get('email');
      const provider = params.get('provider');
      
      if (error) {
        console.log('[CapacitorOAuth] OAuth error received:', error);
        
        // Handle specific error types
        if (error === 'no_account') {
          const email = errorEmail || 'this email';
          alert(`No account found for ${email}.\n\nPlease contact your organization administrator to receive an invitation.`);
          window.location.href = '/auth/login';
          return;
        } else if (error === 'account_mismatch') {
          alert('This OAuth account is linked to a different user. Please try a different sign-in method.');
          window.location.href = '/auth/login';
          return;
        } else if (error === 'token_exchange_failed') {
          alert('Authentication failed. Please try again.');
          window.location.href = '/auth/login';
          return;
        } else {
          alert(`Authentication error: ${error}`);
          window.location.href = '/auth/login';
          return;
        }
      }

      // Extract OAuth success parameters
      const oauthSuccess = params.get('oauth_success');
      const sessionToken = params.get('session_token');
      const accessToken = params.get('access_token');
      const userEmail = params.get('user_email');
      const userId = params.get('user_id');
      const firstName = params.get('first_name');
      const lastName = params.get('last_name');
      const role = params.get('role');
      const isAdmin = params.get('is_admin') === 'true';
      const clientId = params.get('client_id');
      const employeeId = params.get('employee_id');  // Employee ID for HR modules
      const accountLinked = params.get('account_linked') === 'true';
      const requiresMFA = params.get('requires_mfa') === 'true';
      const requiresDeviceVerification = params.get('requires_device_verification') === 'true';
      const mfaMethodsParam = params.get('mfa_methods');
      const deviceVerificationInfo = params.get('device_verification_info');
      const redirectTarget = getPostLoginRedirect(params);

      // Validate required parameters
      if (oauthSuccess !== 'true' || !sessionToken || !userEmail) {
        console.error('[CapacitorOAuth] Invalid callback parameters');
        throw new Error('Invalid OAuth callback parameters');
      }

      // Handle device verification requirement
      if (requiresDeviceVerification) {
        console.log('[CapacitorOAuth] Device verification required');

        const parsedDeviceInfo = (() => {
          if (!deviceVerificationInfo) {
            return {}
          }

          try {
            return JSON.parse(deviceVerificationInfo)
          } catch {
            return { raw: deviceVerificationInfo }
          }
        })()

        setPendingValue('device_verification_pending', JSON.stringify({
          deviceInfo: parsedDeviceInfo,
          preferredMethod: 'email'
        }), { encode: true })
        setPendingValue('oauth_pending_session_token', sessionToken)
        setPendingValue('oauth_pending_user_email', userEmail)
        setPendingValue('session_token', sessionToken)
        if (userId) setPendingValue('oauth_pending_user_id', userId)
        if (provider) setPendingValue('oauth_pending_provider', provider)

        if (requiresMFA && mfaMethodsParam) {
          const mfaMethods = JSON.parse(mfaMethodsParam)
          setPendingValue('mfa_pending_context', JSON.stringify({
            provider,
            accountLinked,
            mfaMethods,
            isOAuthLogin: true,
            requiresDeviceVerification: true,
            userId: userId || null,
            email: userEmail,
            sessionToken
          }), { encode: true })
        }

        // Navigate to device verification
        window.location.href = appendPostLoginRedirect('/auth/device-verification?oauth_login=true', redirectTarget);
        return;
      }

      // Handle MFA requirement
      if (requiresMFA && mfaMethodsParam) {
        console.log('[CapacitorOAuth] MFA required');

        // Store OAuth context for MFA
        setPendingValue('oauth_pending_session_token', sessionToken)
        setPendingValue('oauth_pending_user_email', userEmail)
        setPendingValue('session_token', sessionToken)
        if (userId) setPendingValue('oauth_pending_user_id', userId)
        if (provider) setPendingValue('oauth_pending_provider', provider)

        const mfaMethods = JSON.parse(mfaMethodsParam);
        setPendingValue('mfa_pending_context', JSON.stringify({
          provider,
          accountLinked,
          mfaMethods,
          isOAuthLogin: true,
          userId: userId || null,
          email: userEmail,
          sessionToken
        }), { encode: true });

        // Navigate to MFA page
        window.location.href = appendPostLoginRedirect('/auth/mfa?oauth_login=true', redirectTarget);
        return;
      }

      // Direct authentication - process the OAuth login
      console.log('[CapacitorOAuth] Processing direct OAuth authentication');

      // Build user data object
      const userData = {
        id: parseInt(userId || '0'),
        email: userEmail,
        first_name: firstName || '',
        last_name: lastName || '',
        role: role || 'user',
        is_admin: isAdmin,
        client_id: clientId || '',
        employee_id: employeeId ? parseInt(employeeId) : null,  // Include employee_id for HR modules
        is_email_verified: true
      };

      console.log('[CapacitorOAuth] User data with employee_id:', { ...userData, employee_id: userData.employee_id });

      // Use authService to set authentication (handles session monitoring)
      console.log('[CapacitorOAuth] Setting authentication via authService');
      await authService.setOAuthAuth(sessionToken, userData, accessToken);

      // Verify authentication
      const isAuth = authService.isAuthenticated();
      const user = authService.getUser();
      
      console.log('[CapacitorOAuth] Auth verification:', {
        isAuthenticated: isAuth,
        hasUser: !!user,
        userEmail: user?.email
      });

      if (!isAuth || !user) {
        throw new Error('Failed to set authentication state');
      }

      // Stop listening for callbacks (we're done)
      await this.stopListening();

      clearPendingValue('device_verification_pending');
      clearPendingValue('mfa_pending_context');
      clearPendingValue('oauth_pending_session_token');
      clearPendingValue('oauth_pending_user_email');
      clearPendingValue('oauth_pending_user_id');
      clearPendingValue('oauth_pending_provider');

      console.log('[CapacitorOAuth] OAuth authentication successful, redirecting to:', redirectTarget);

      // Navigate to intended post-login route
      window.location.href = redirectTarget;

    } catch (error) {
      console.error('[CapacitorOAuth] Error processing OAuth callback:', error);
      
      // Reset processing state on error
      this.isProcessing = false;
      if (this.processingTimeout) {
        clearTimeout(this.processingTimeout);
        this.processingTimeout = null;
      }
      
      // Show error to user
      alert('OAuth authentication failed. Please try again.');
      
      // Navigate to login with error
      window.location.href = '/auth/login?error=oauth_callback_failed';
    }
  }

  /**
   * Initiate OAuth flow for Capacitor mobile app
   * Opens system browser with OAuth URL, app handles callback via deep link
   */
  async initiateOAuth(provider: 'google' | 'microsoft' | 'intuit' | 'xero', redirectTo?: string): Promise<void> {
    console.log('[CapacitorOAuth] Initiating OAuth for provider:', provider);
    
    const backendUrl = getPublicRuntimeConfig().baseUrl || 'https://totalaccess.co.za';
    
    // Get device-specific app_id so the OAuth callback creates the session with correct identity
    let appId = '';
    try {
      const { deviceService } = await import('./capacitorDeviceService');
      // deviceService.getAppId() is the canonical way to get the app_id
      appId = deviceService.getAppId() || localStorage.getItem('capacitor_app_id') || '';
      console.log('[CapacitorOAuth] Including app_id in OAuth flow:', appId);
    } catch (err) {
      // Fallback to localStorage directly
      try {
        appId = localStorage.getItem('capacitor_app_id') || '';
      } catch (_) {}
      console.warn('[CapacitorOAuth] Could not get app_id via deviceService, fallback:', appId, err);
    }
    
    // Mobile apps use deep link callback and preserve intended post-login navigation
    const sanitizedRedirect = sanitizePostLoginRedirect(redirectTo, '');
    const redirectUrl = sanitizedRedirect
      ? `totalaccess://oauth-callback?redirect=${encodeURIComponent(sanitizedRedirect)}`
      : 'totalaccess://oauth-callback';
    let oauthUrl = `${backendUrl}/api/v1/auth/oauth/${provider}?redirect_url=${encodeURIComponent(redirectUrl)}&client_type=mobile`;
    if (appId) {
      oauthUrl += `&app_id=${encodeURIComponent(appId)}`;
    }
    
    console.log('[CapacitorOAuth] Opening system browser with OAuth URL:', oauthUrl);
    
    // Open OAuth URL in system browser (Chrome Custom Tabs / SFSafariViewController)
    // windowName defaults to _blank so the deep-link callback can route back to the app
    await Browser.open({ url: oauthUrl });
  }
}

// Export singleton instance
export const capacitorOAuthHandler = new CapacitorOAuthHandler();
